import { useRef, useEffect } from 'react'
import type { MediaRef } from '../utility'
import {
	isPaused,
	isMissionPauseActive,
	useMissionStateSelector,
	useAudioOutputId,
	getMissionPhase,
} from '../context'
import {
	MISSION_PHASES,
	typeof MISSION_PHASES as MissionPhasesObject,
} from '@mission.io/mission-toolkit/constants'

const QUIET_PHASES = [
	MISSION_PHASES.LOGIN,
	MISSION_PHASES.TRAINING,
	MISSION_PHASES.SURVEY_STANDBY,
	MISSION_PHASES.REVIEW_RESULTS,
	MISSION_PHASES.CLOSED,
]

/**
 * This hook creates a special media ref, or causes a given ref to become "silent" whenever the current phase of the mission
 * is part of an array of `silentPhases`. By default, that is every phase except the ACTIVE phase.
 */
export function useSilentMissionPhaseRef({
	silentPhases = QUIET_PHASES,
	ref: passedRef = null,
}: {
	silentPhases?: $Keys<MissionPhasesObject>[],
	ref?: ?MediaRef,
} = {}): MediaRef {
	const newRef = useRef(null)
	const ref = passedRef || newRef
	const missionPhase = useMissionStateSelector(getMissionPhase)
	useEffect(() => {
		const currentRef = ref.current
		if (currentRef) {
			if (!missionPhase || silentPhases.includes(missionPhase)) {
				if (!currentRef.paused) {
					currentRef.pause()
				}
			} else if (currentRef.paused && (!currentRef.ended || currentRef.loop)) {
				currentRef.play().catch(err => {
					throw new Error(
						`Failed to play "silent mission phase" media (src: ${currentRef.currentSrc}): ${err.message}`
					)
				})
			}
		}
	}, [missionPhase, ref, silentPhases])
	return ref
}

/**
 * This hook will pause the ref if the mission is paused, and play the ref
 * if the mission is not paused.
 * @param {MediaRef} ref The item ref
 * @param {?boolean} applyMissionPause The flag that indicates the
 * ref only apply for PauseAction and not for MissionPause
 */
export function useMissionPauseForRef(
	ref: MediaRef,
	{
		applyMissionPause = true,
		autoPlay = true,
	}: { applyMissionPause?: boolean, autoPlay?: boolean } = {}
) {
	const pause = useMissionStateSelector(isPaused)
	const missionPause = useMissionStateSelector(isMissionPauseActive)
	const hasStarted = useRef(false)
	const missionPausedActive = pause || (applyMissionPause && missionPause)

	useEffect(() => {
		const checkToPause = () => {
			if (hasStarted.current !== true) {
				hasStarted.current = true
			}
			if (missionPausedActive && ref.current) {
				ref.current.pause()
				return
			}
		}
		const currentRef = ref?.current
		if (currentRef) {
			currentRef.addEventListener('play', checkToPause)
		}
		return () => {
			if (currentRef) {
				currentRef.removeEventListener('play', checkToPause)
			}
		}
	}, [ref, missionPausedActive])

	useEffect(() => {
		const currentRef = ref.current
		if (missionPausedActive !== null && currentRef) {
			if (missionPausedActive) {
				currentRef.pause()
			} else if (!currentRef.ended || currentRef.loop) {
				if (hasStarted.current !== true && !autoPlay) return
				currentRef.play().catch(() => {
					// We catch here so that we can handle `The play() request was interrupted by a call to pause()` error.
				})
			}
		}
	}, [missionPausedActive, ref, autoPlay])
}
/**
 * This hook will make sure that the sinkId of the provided ref is always correctly synced with the selected audio output.
 */
export function useSelectedAudioOutput(ref: MediaRef) {
	const audioOutputId: ?string = useAudioOutputId()

	const current = ref.current
	useEffect(() => {
		if (!audioOutputId) {
			return
		}

		const element = ref.current
		if (!element) {
			return
		}

		// $FlowFixMe setSinkId is a real thing
		element.setSinkId(audioOutputId)
	}, [ref, current, audioOutputId])
}
